import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided, DraggableProvided, DraggableStateSnapshot } from "@hello-pangea/dnd";
import { useBuilder, BlockConfig } from "./BuilderContext";
import EditableBlockWrapper from "./EditableBlockWrapper";
import HeaderBlock from "./blocks/HeaderBlock";
import RecipientBlock from "./blocks/RecipientBlock";
import MatterDetailsBlock from "./blocks/MatterDetailsBlock";
import FeesTableBlock from "./blocks/FeesTableBlock";
import DisbursementsTableBlock from "./blocks/DisbursementsTableBlock";
import TimeEntriesTableBlock from "./blocks/TimeEntriesTableBlock";
import TotalsSummaryBlock from "./blocks/TotalsSummaryBlock";
import PaymentHistoryBlock from "./blocks/PaymentHistoryBlock";
import InvoiceHistoryBlock from "./blocks/InvoiceHistoryBlock";
import PerMatterBreakdownBlock from "./blocks/PerMatterBreakdownBlock";
import NotesBlock from "./blocks/NotesBlock";
import FooterBlock from "./blocks/FooterBlock";
import ImageBlock from "./blocks/ImageBlock";
import SignatureBlock from "./blocks/SignatureBlock";
import PageBreakBlock from "./blocks/PageBreakBlock";

// ── Constants ────────────────────────────────────────────────────────────
const PAGE_WIDTH = 816; // 8.5" at 96dpi
const PAGE_HEIGHT = 1056; // 11" at 96dpi
const PAGE_PADDING_Y = 96; // 48px top + 48px bottom
const CONTENT_HEIGHT = PAGE_HEIGHT - PAGE_PADDING_Y; // 960px
const BLOCK_GAP = 32; // mb-8 = 2rem

// ── Block component registry ─────────────────────────────────────────────
const BLOCK_COMPONENTS: Record<string, React.FC<{ block: BlockConfig }>> = {
  header: HeaderBlock,
  recipient: RecipientBlock,
  matter_details: MatterDetailsBlock,
  fees_table: FeesTableBlock,
  disbursements_table: DisbursementsTableBlock,
  time_entries_table: TimeEntriesTableBlock,
  totals_summary: TotalsSummaryBlock,
  payment_history: PaymentHistoryBlock,
  invoice_history: InvoiceHistoryBlock,
  per_matter_breakdown: PerMatterBreakdownBlock,
  notes: NotesBlock,
  footer: FooterBlock,
  image: ImageBlock,
  signature: SignatureBlock,
  page_break: PageBreakBlock,
};

// ── Page distribution ────────────────────────────────────────────────────
function distributeBlocks(
  blocks: BlockConfig[],
  heights: Map<string, number>
): BlockConfig[][] {
  const pages: BlockConfig[][] = [[]];
  let currentHeight = 0;

  for (const block of blocks) {
    // page_break forces a new page but doesn't render on any page itself
    if (block.blockType === "page_break") {
      if (pages[pages.length - 1].length > 0) {
        pages.push([]);
        currentHeight = 0;
      }
      continue;
    }

    const blockHeight = heights.get(block.id) || 80;
    const needed = blockHeight + (currentHeight > 0 ? BLOCK_GAP : 0);

    if (currentHeight + needed > CONTENT_HEIGHT && pages[pages.length - 1].length > 0) {
      pages.push([]);
      currentHeight = 0;
    }

    pages[pages.length - 1].push(block);
    currentHeight += blockHeight + (currentHeight > 0 ? BLOCK_GAP : 0);
  }

  return pages;
}

// ── Component ────────────────────────────────────────────────────────────
const DocumentPreview: React.FC = () => {
  const { state, dispatch } = useBuilder();

  // Enabled blocks sorted by order (flat list for DnD)
  const enabledBlocks = useMemo(
    () =>
      [...state.blocks]
        .filter((b) => b.enabled)
        .sort((a, b) => a.order - b.order),
    [state.blocks]
  );

  // ── Block height measurement ─────────────────────────────────────────
  const blockRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [blockHeights, setBlockHeights] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      setBlockHeights((prev) => {
        const next = new Map(prev);
        let changed = false;
        for (const entry of entries) {
          const blockId = entry.target.getAttribute("data-block-id");
          if (blockId) {
            const h = Math.ceil(entry.contentRect.height);
            if (next.get(blockId) !== h) {
              next.set(blockId, h);
              changed = true;
            }
          }
        }
        return changed ? next : prev;
      });
    });

    blockRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [enabledBlocks]);

  // Ref callback for each block element
  const setBlockRef = useCallback(
    (blockId: string) => (el: HTMLDivElement | null) => {
      if (el) {
        blockRefs.current.set(blockId, el);
        el.setAttribute("data-block-id", blockId);
      } else {
        blockRefs.current.delete(blockId);
      }
    },
    []
  );

  // ── Paginate blocks ──────────────────────────────────────────────────
  const pages = useMemo(
    () => distributeBlocks(enabledBlocks, blockHeights),
    [enabledBlocks, blockHeights]
  );

  // ── DnD handler ──────────────────────────────────────────────────────
  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;

      const items = Array.from(enabledBlocks);
      const [moved] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, moved);

      // Validate: totals_summary must stay after all line-item tables
      const lineItemTypes = ["fees_table", "disbursements_table", "time_entries_table"];
      const totalsIdx = items.findIndex((b) => b.blockType === "totals_summary");
      const lastLineIdx = Math.max(
        ...items.map((b, i) => (lineItemTypes.includes(b.blockType) ? i : -1))
      );
      if (totalsIdx >= 0 && lastLineIdx >= 0 && totalsIdx < lastLineIdx) return;

      // Rebuild full block list: merge reordered enabled blocks with disabled ones
      const disabledBlocks = state.blocks.filter((b) => !b.enabled);
      const allBlocks = [...items, ...disabledBlocks];
      dispatch({ type: "REORDER_BLOCKS", blocks: allBlocks });
    },
    [enabledBlocks, state.blocks, dispatch]
  );

  // ── Click-outside deselects ──────────────────────────────────────────
  const handleBackgroundClick = useCallback(() => {
    dispatch({ type: "SELECT_BLOCK", blockId: null });
  }, [dispatch]);

  return (
    <div onClick={handleBackgroundClick}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="document-blocks">
          {(droppableProvided: DroppableProvided) => (
            <div
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
            >
              {/* Render pages */}
              {pages.map((pageBlocks, pageIdx) => (
                <React.Fragment key={pageIdx}>
                  {/* Page boundary indicator between pages */}
                  {pageIdx > 0 && (
                    <div className="flex items-center gap-4 my-6 print:hidden">
                      <div className="flex-1 border-t border-dashed border-slate-300" />
                      <span className="text-xs text-slate-400">
                        Page {pageIdx + 1}
                      </span>
                      <div className="flex-1 border-t border-dashed border-slate-300" />
                    </div>
                  )}

                  {/* Paper page */}
                  <div
                    className="relative mx-auto mb-8"
                    style={{ width: PAGE_WIDTH }}
                  >
                    {/* Shadow layers for depth effect */}
                    <div className="absolute inset-0 bg-white rounded-sm shadow-md transform rotate-[0.3deg] translate-x-[2px] translate-y-[2px] print:hidden" />
                    <div className="absolute inset-0 bg-white rounded-sm shadow-sm transform rotate-[0.15deg] translate-x-[1px] translate-y-[1px] print:hidden" />

                    {/* Page content */}
                    <div
                      className={`relative bg-white rounded-sm print:rounded-none ${
                        pageIdx > 0 ? "print-page-break" : ""
                      }`}
                      style={{
                        minHeight: PAGE_HEIGHT,
                        boxShadow:
                          "0 1px 3px rgba(0,0,0,0.08), 0 8px 30px rgba(0,0,0,0.07)",
                      }}
                    >
                      <div className="px-12 py-12 print:px-8 print:py-8">
                        {pageBlocks.map((block) => {
                          const Component = BLOCK_COMPONENTS[block.blockType];
                          if (!Component) return null;
                          const globalIndex = enabledBlocks.findIndex(
                            (b) => b.id === block.id
                          );

                          return (
                            <Draggable
                              key={block.id}
                              draggableId={block.id}
                              index={globalIndex}
                            >
                              {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                                <EditableBlockWrapper
                                  block={block}
                                  provided={provided}
                                  snapshot={snapshot}
                                >
                                  <div
                                    ref={setBlockRef(block.id)}
                                    className="mb-8 last:mb-0"
                                  >
                                    <Component block={block} />
                                  </div>
                                </EditableBlockWrapper>
                              )}
                            </Draggable>
                          );
                        })}
                      </div>
                    </div>

                    {/* Page number */}
                    <div className="text-center mt-2 text-xs text-slate-400 print:hidden">
                      Page {pageIdx + 1} of {pages.length}
                    </div>
                  </div>
                </React.Fragment>
              ))}

              {droppableProvided.placeholder}

              {/* Empty state */}
              {enabledBlocks.length === 0 && (
                <div className="mx-auto" style={{ width: PAGE_WIDTH }}>
                  <div
                    className="relative bg-white rounded-sm"
                    style={{
                      minHeight: PAGE_HEIGHT,
                      boxShadow:
                        "0 1px 3px rgba(0,0,0,0.08), 0 8px 30px rgba(0,0,0,0.07)",
                    }}
                  >
                    <div className="flex items-center justify-center h-full py-40">
                      <div className="text-center text-slate-400">
                        <p className="text-lg font-medium">No blocks enabled</p>
                        <p className="text-sm mt-1.5 text-slate-300">
                          Open the block drawer to add sections
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default DocumentPreview;
